/* eslint-disable react/jsx-one-expression-per-line */
import PeAvatar from '@pe-libs/react-components/build/components/PeAvatar';
import InfoCircleOutlined from '@pe-libs/react-components/build/components/PeIcons/InfoCircleOutlined.svg';
import PeTooltip from '@pe-libs/react-components/build/components/PeTooltip';
import PeTypography from '@pe-libs/react-components/build/components/PeTypography';
import useIntlMessage from '@pe-libs/react-components/build/lib/intl/hooks/useIntlMessage';
import useIntlNumber from '@pe-libs/react-components/build/lib/intl/hooks/useIntlNumber';
import React, { FC } from 'react';
import { InferType } from 'yup';
import Link from '#modules/expertDirectory/components/Link';
import { singleResultSchema } from '#modules/expertDirectory/schemas/searchDirectory';
import Awards from './Awards';
import { getSourcesText } from './helper';
import messages from './messages';
import Stars from './Stars';
import useStyles from './styles';

type Props = {
  ratings: InferType<typeof singleResultSchema>['ratings'];
  avatar: string;
  url: string;
  scenario: string;
  title: string;
};

const Details: FC<Props> = ({
  ratings: {
    aggregatedProfileCount,
    averageRatingScore,
    totalRatingCount,
    totalRatingCountWithUnpublished,
    totalReviewCount,
    externalRatingServicesCount,
    awards,
  },
  avatar,
  url,
  scenario,
  title,
}) => {
  const styles = useStyles();
  const formatMessage = useIntlMessage();
  const formatNumber = useIntlNumber();
  const reviewsText =
    totalRatingCount && totalRatingCount > 1 ? formatMessage(messages.reviews) : formatMessage(messages.review);

  const tooltipContent = formatMessage(messages.noStarsTooltip, {
    br: '',
    title: <div className={styles.tooltipTitle}>{formatMessage(messages.noStarsTooltipTitle, { br: <br /> })}</div>,
    listItem: (
      <ul className={styles.tooltipList}>
        <li className={styles.tooltipListItem}>{formatMessage(messages.noStarsTooltipListItem1)}</li>
        <li className={styles.tooltipListItem}>{formatMessage(messages.noStarsTooltipListItem2)}</li>
        <li className={styles.tooltipListItem}>{formatMessage(messages.noStarsTooltipListItem3)}</li>
      </ul>
    ),
  }) as React.ReactChild;

  return (
    <>
      <div className={styles.detailsContainer}>
        <div className={styles.avatarAndStarsContainer}>
          <div className={styles.avatarContainer}>
            <PeAvatar imgProps={{ width: 46, height: 46 }} variant="square" src={avatar} alt={`${title} logo`} />
          </div>
          <div>
            <Stars
              averageRatingScore={averageRatingScore as number}
              totalRatingCount={totalRatingCount as number}
              scenario={scenario}
            />
            {totalRatingCount && totalRatingCount > 0 ? (
              // eslint-disable-next-line unicorn/no-nested-ternary
              scenario === 'a' ? (
                <Link title={formatMessage(messages.hoverRatings, { title })} href={url}>
                  {formatNumber(totalRatingCount, { minimumFractionDigits: 0 })} {reviewsText}
                </Link>
              ) : (
                <div className={styles.tooltipNoStarsContainer}>
                  <Link href={url} title={formatMessage(messages.hoverRatings, { title })}>
                    <PeTypography variant="body2">
                      {formatNumber(totalRatingCount, { minimumFractionDigits: 0 })} {reviewsText}
                    </PeTypography>
                  </Link>
                  <PeTooltip className={styles.noStarsTooltip} title={tooltipContent}>
                    <InfoCircleOutlined />
                  </PeTooltip>
                </div>
              )
            ) : (
              <span>{formatMessage(messages.noReviews)}</span>
            )}

            {totalRatingCount && totalRatingCount > 0 && (
              <span className={scenario === 'b' ? styles.sourcesTextFlex : styles.sourcesText}>
                {scenario === 'b' && (
                  <PeTypography variant="body2">
                    {formatMessage(messages.reviewsPublished, {
                      value: formatNumber(totalRatingCountWithUnpublished as number, { minimumFractionDigits: 0 }),
                    })}
                  </PeTypography>
                )}
                {getSourcesText(
                  formatMessage,
                  (externalRatingServicesCount as number) + 1,
                  externalRatingServicesCount as number,
                  totalReviewCount as number,
                )}
              </span>
            )}
          </div>
        </div>
        <Awards data={awards} />
      </div>
      {aggregatedProfileCount && aggregatedProfileCount > 1 && (
        <div className={styles.detailsDesc}>
          {formatMessage(messages.profileLocations, {
            link: (
              <Link href={url}>
                {formatMessage(messages.profileLocationsLink, { locations: aggregatedProfileCount })}
              </Link>
            ),
          })}
        </div>
      )}
    </>
  );
};

export default Details;
